import type { ThemeName } from '@domain/models/theme.enum';
import type { UiPreferences } from '@domain/models/ui-preferences.interface';
import type { IUserPreferencesRepository } from '@domain/data-contracts/user-preferences-repository.interface';

const DEFAULT_DEBOUNCE_MS = 500;

type Options = {
  readonly debounceMs?: number;
};

type ScheduleFn = (cb: () => void, delay: number) => ReturnType<typeof setTimeout>;
type CancelFn = (handle: ReturnType<typeof setTimeout>) => void;

/**
 * Domain service on top of {@link IUserPreferencesRepository}.
 *
 * The theme picker calls `setTheme(...)` every time the user clicks
 * a radio option. To avoid thrashing the back-end when someone is
 * "shopping around" between themes, writes are debounced by
 * {@link Options.debounceMs} (default 500 ms). Errors are swallowed
 * here — the caller may subscribe to the returned promise from
 * {@link flushPending} if it needs per-write feedback.
 */
export class UserPreferencesService {
  private readonly debounceMs: number;
  private readonly schedule: ScheduleFn;
  private readonly cancel: CancelFn;

  private themeHandle: ReturnType<typeof setTimeout> | null = null;
  private pendingTheme: ThemeName | null = null;
  private themeInflight: Promise<void> | null = null;

  private languageHandle: ReturnType<typeof setTimeout> | null = null;
  private pendingLanguage: UiPreferences['language'] | null = null;
  private languageInflight: Promise<void> | null = null;

  public constructor(
    private readonly repo: IUserPreferencesRepository,
    options: Options = {},
    schedule: ScheduleFn = setTimeout,
    cancel: CancelFn = clearTimeout,
  ) {
    this.debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;
    this.schedule = schedule;
    this.cancel = cancel;
  }

  public get(): Promise<UiPreferences> {
    return this.repo.get();
  }

  /**
   * Persist the user's theme choice. Successive calls within
   * `debounceMs` collapse into a single PATCH with the latest value.
   */
  public setTheme(theme: ThemeName): void {
    this.pendingTheme = theme;
    if (this.themeHandle !== null) {
      this.cancel(this.themeHandle);
    }
    this.themeHandle = this.schedule(() => {
      void this.flushTheme();
    }, this.debounceMs);
  }

  /**
   * Persist the user's language choice. Debounced exactly like
   * {@link setTheme}.
   */
  public setLanguage(language: UiPreferences['language']): void {
    this.pendingLanguage = language;
    if (this.languageHandle !== null) {
      this.cancel(this.languageHandle);
    }
    this.languageHandle = this.schedule(() => {
      void this.flushLanguage();
    }, this.debounceMs);
  }

  /**
   * Flush both pending debounced writes immediately. Returns a
   * promise that resolves once the underlying repository calls
   * settle (failures are caught and logged, never rejected).
   */
  public flushPending(): Promise<void> {
    return Promise.all([this.flushTheme(), this.flushLanguage()]).then(
      () => undefined,
    );
  }

  private flushTheme(): Promise<void> {
    if (this.themeHandle !== null) {
      this.cancel(this.themeHandle);
      this.themeHandle = null;
    }
    if (this.pendingTheme === null) {
      return this.themeInflight ?? Promise.resolve();
    }
    const theme = this.pendingTheme;
    this.pendingTheme = null;
    this.themeInflight = this.repo
      .updateTheme(theme)
      .catch((error: unknown) => {
        console.warn('[user-preferences] updateTheme failed', error);
      })
      .finally(() => {
        this.themeInflight = null;
      });
    return this.themeInflight;
  }

  private flushLanguage(): Promise<void> {
    if (this.languageHandle !== null) {
      this.cancel(this.languageHandle);
      this.languageHandle = null;
    }
    if (this.pendingLanguage === null) {
      return this.languageInflight ?? Promise.resolve();
    }
    const language = this.pendingLanguage;
    this.pendingLanguage = null;
    this.languageInflight = this.repo
      .updateLanguage(language)
      .catch((error: unknown) => {
        console.warn('[user-preferences] updateLanguage failed', error);
      })
      .finally(() => {
        this.languageInflight = null;
      });
    return this.languageInflight;
  }
}
